import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {keepPreviousData} from '@tanstack/react-query';

import {JumboMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  Toast,
  ToastTitle,
  ToastBody,
  TOAST_SEMANTIC,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useApiQuery} from 'src/hooks/useApiQuery';
import {useSpeechToText} from 'src/hooks/useSpeechToText';
import {getCurrentAgent} from 'src/selectors/accounts';
import {useHistory} from 'src/rerouter';
import {ApiError, captureApiError} from 'src/utils/errors';
import logger from 'src/utils/logger';
import {
  Editor,
  type EditorHandle,
  type SuggestionItem,
} from 'src/components/agentic-ui/editor';
import {type Part} from 'src/components/agentic-ui';
import {startNewConversation} from 'src/components/agentic-ui/ask-ai-store';
import {MicControl} from 'src/components/agentic-ui/voice/mic-control';
import {RecordingActions} from 'src/components/agentic-ui/voice/recording-actions';
import {toSuggestionSections} from 'src/utils/askai-typeahead';
import type {TypeaheadApiItem} from 'src/types/askai-typeahead';
import AiOrb from 'src/components/dashboard/ai-orb';

import css from './askai-typeahead.css';


const SOURCE = 'askai_typeahead';

// Grace service requires a custom apiPath prefix instead of the default /api/v1/.
const GRACE_API_OPTIONS = {options: {apiPath: '/grace/api/v1/'}} as const;

function AskAiTypeahead(): React.ReactElement {
  const {t} = useLabelTranslation();
  const history = useHistory();

  // Same source as the app-shell account bar: the agent's full display name.
  const agent = useSelector(getCurrentAgent);
  const name = agent?.name || '';

  const editorRef = useRef<EditorHandle | null>(null);

  // Streaming speech-to-text, wired the same way as the chat-panel composer:
  // interim (faded) + final (corrected) transcript written live into the input.
  const stt = useSpeechToText({
    onInterim: (text) => editorRef.current?.pushInterim(text),
    onFinal: (text) => editorRef.current?.pushFinal(text),
    onError: (err) => {
      if (err.code === 'permission-denied') {
        return;
      }
      const noMic = err.code === 'no-microphone';
      const title = noMic
        ? t('No_microphone_found', 'No microphone found')
        : t('Microphone_Connection_Failed', 'Microphone Connection Failed');
      const body = noMic
        ? t(
            'Connect_a_microphone_and_try_again',
            'Connect a microphone and try again',
          )
        : t(
            'Selected_microphone_cannot_be_connected_Use_other_microphone_or_try_again',
            'Selected microphone cannot be connected. Use other microphone or try again',
          );
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>{title}</ToastTitle>
          <ToastBody>{body}</ToastBody>
        </Toast>,
        {timeout: 6000},
      );
    },
  });

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce keystrokes before issuing a typeahead request.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  const trimmedQuery = debouncedQuery.trim();

  const {data, isFetching, error} = useApiQuery<TypeaheadApiItem[]>(
    {pathname: 'typeahead', query: {q: trimmedQuery}},
    {
      queryOptions: {
        enabled: trimmedQuery.length > 0,
        // Keep showing the previous query's suggestions until the new ones land.
        placeholderData: keepPreviousData,
      },
      apiOptions: GRACE_API_OPTIONS,
    },
  );

  useEffect(() => {
    if (error instanceof ApiError) {
      captureApiError(error);
    }
  }, [error]);

  // keepPreviousData fails when fast typing chains pending queries (the
  // "previous" query has no data yet) — hold the last real results instead.
  const lastSuggestionsRef = useRef<ReturnType<typeof toSuggestionSections>>(
    [],
  );
  const suggestions = useMemo(() => {
    // Guard: placeholder data survives even when the query is disabled, so an
    // emptied input would otherwise keep showing the last results.
    if (!trimmedQuery) {
      lastSuggestionsRef.current = [];
      return lastSuggestionsRef.current;
    }
    if (data == null && isFetching) {
      return lastSuggestionsRef.current;
    }
    lastSuggestionsRef.current = toSuggestionSections(data ?? [], t);
    return lastSuggestionsRef.current;
  }, [data, t, trimmedQuery, isFetching]);

  const startConversation = useCallback((message: string) => {
    const trimmed = message.trim();
    if (!trimmed) {
      return;
    }
    const started = startNewConversation({source: SOURCE, message: trimmed});
    if (!started) {
      logger.info(
        'askai typeahead: chat panel not mounted, cannot start conversation',
      );
    }
  }, []);

  const handleSelect = useCallback(
    (item: SuggestionItem) => {
      if (item.meta === 'pages') {
        void history.push(item.key);
        return;
      }
      startConversation(item.label);
    },
    [history, startConversation],
  );

  const handleSend = useCallback(
    (parts: Part[]) => {
      const textPart = parts.find((p) => p.type === 'plain_text');
      const text =
        textPart != null && textPart.type === 'plain_text' ? textPart.data : '';
      startConversation(text);
    },
    [startConversation],
  );

  return (
    <div className={css.container}>
      <div className={css.orbRing}>
        <AiOrb className={css.orb} />
      </div>
      <JumboMedium
        className={css.greeting}
        highlightString={name}
        highlightedTextClassName={css.greetingName}
      >
        {name
          ? t('home_greeting', {
              defaultValue: 'Hey {{name}}, what would you like to explore?',
              name,
            })
          : t(
              'Hey_there_what_would_you_like_to_explore',
              'Hey there, what would you like to explore?',
            )}
      </JumboMedium>
      <div className={css.inputWrap}>
        <Editor
          ref={editorRef}
          enableSuggestions
          enableMentions={false}
          enableUpload={false}
          suggestions={suggestions}
          suggestionsLoading={isFetching}
          onQueryChange={setQuery}
          onSelectSuggestion={handleSelect}
          onSend={handleSend}
          placeholder={
            stt.state === 'connecting'
              ? t('Connecting', 'Connecting...')
              : stt.state === 'recording' || stt.state === 'stopping'
              ? t('Listening', 'Listening...')
              : t(
                  'Search_for_any_page_report_or_type_your_query',
                  'Search for any page, report or type your query',
                )
          }
          isRecording={stt.state !== 'idle'}
          micControl={
            stt.state === 'idle' ? (
              <MicControl
                permission={stt.permission}
                devices={stt.devices}
                selectedDeviceId={stt.selectedDeviceId}
                onSelectDevice={stt.setSelectedDeviceId}
                micLabel={t('Start_voice_input', 'Start voice input')}
                defaultMicLabel={t('Default_Microphone', 'Default Microphone')}
                micPromptTooltip={t(
                  'We\'ll_ask_for_mic_access_please_click_Allow_to_start_speechtotext',
                  '🎙️ We\'ll ask for mic access — please click "Allow" to start speech-to-text.',
                )}
                micDeniedTooltip={t(
                  'Mic_access_blocked_Enable_it_in_your_browser\'s_site_settings_to_use_speechtotext',
                  '⚠️ Mic access blocked. Enable it in your browser\'s site settings to use speech-to-text.',
                )}
                onMicClick={() => {
                  editorRef.current?.beginVoice();
                  void stt.start();
                }}
              />
            ) : undefined
          }
          bottomBarActions={
            stt.state === 'connecting' || stt.state === 'stopping' ? (
              <CircularLoader size="small" />
            ) : stt.state === 'recording' ? (
              <RecordingActions
                cancelLabel={t('Cancel_recording', 'Cancel recording')}
                confirmLabel={t('Stop_recording', 'Stop recording')}
                onConfirm={() => {
                  editorRef.current?.endVoice();
                  void stt.stop();
                }}
                onCancel={() => {
                  editorRef.current?.cancelVoice();
                  stt.cancel();
                }}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
}

export default AskAiTypeahead;
